import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from 'semantic-ui-react';
import { applyFilterCfg } from '../../../../../components/financialTables/configuration/applyUtill';
import {
    ConfigProps,
    DEFAULT_FILTER_AND_CFG,
    DEFAULT_TABLE_CFG,
    PropValues,
} from '../../../../../components/financialTables/configuration/constants';
import CloneUtils from '../../../../../utils/cloneUtils';
import CollectionUtils from '../../../../../utils/collections';
import Utils from '../../../../../utils/descriptor/descriptorUtils';
import { ReportsUtils } from '../../../../../utils/reportsUtils';
import ReportsRepo from '../../../../../utils/repository/repoertsRepo';
import Table from '../../../../company/components/fiancialTable/table';
import { ColumnMetadataType, FormatType, MasterDescriptorType, PeriodType, SchemaType } from '../../types';
import { useColumnsMetadata } from '../../stores/metadataStore';
import { COLUMN_TYPE, PERIOD } from '../../constants/generalConstants';
import { useHighlightsStoreHighlightedDescriptorId } from '../../stores/highlightStore';
import _ from 'lodash';
import FixedElementOnScroll from '../../../../../components/fixedElementOnScroll';

type MasterTablePropsType = {
    schema: SchemaType;
};

const MasterTable = ({ schema }: MasterTablePropsType) => {
    const masterTableRef = useRef<HTMLDivElement>(null);
    const highlightedDescriptorId = useHighlightsStoreHighlightedDescriptorId();
    const colsMetadata = useColumnsMetadata();

    const filtersOverride = useMemo(() => getFilters(colsMetadata), [colsMetadata]);

    const descriptors = schema.descriptors;
    const dynamicFlatDescriptors: MasterDescriptorType[] = useMemo(
        // @ts-ignore
        () => Utils.flatDescriptors(descriptors, true),
        [descriptors]
    );
    // @ts-ignore
    const reportsUtil = useMemo(() => new ReportsUtils(dynamicFlatDescriptors), [dynamicFlatDescriptors]);

    const [reports, setReports] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    // Provenance is a large payload only Source view needs, so the reports load without it and are
    // re-fetched with it once a user opens Source view. Sticky, so later reloads keep it.
    const [provenanceRequested, setProvenanceRequested] = useState(false);

    useEffect(() => {
        setLoading(true);
        ReportsRepo.listBySchema(schema.id, (nonCalculatedReports: any) => {
            const calculatedReports = reportsUtil.getCalculatedReports(nonCalculatedReports, (a: any) => a, false);

            setReports(nonCalculatedReports.concat(calculatedReports));
            setLoading(false);
        }, undefined, provenanceRequested);
    }, [schema, reportsUtil, provenanceRequested]);

    const dynamicFlatDescriptorsMap = useMemo(
        () =>
            CollectionUtils.arrToMap(
                // @ts-ignore
                dynamicFlatDescriptors,
                (d: MasterDescriptorType) => d.id
            ),
        [dynamicFlatDescriptors]
    );

    const filterCfg = useMemo(() => {
        const config = CloneUtils.deepClone(DEFAULT_FILTER_AND_CFG);
        config[ConfigProps.ORIGINAL_VERSION_CONDITIONAL_FILTER] = ConfigProps.CONDITIONAL_ORIGINAL_DISABLED;
        config[ConfigProps.REPORTS_ORDER] = PropValues.DESC;
        if (filtersOverride.from) {
            config[ConfigProps.YEARS_FILTER].from = filtersOverride.from;
        }

        if (filtersOverride.to) {
            config[ConfigProps.YEARS_FILTER].to = filtersOverride.to;
        }

        if (filtersOverride.periods) {
            config[ConfigProps.PERIODS_FILTER] = filtersOverride.periods;
        }

        if (filtersOverride.formats) {
            config[ConfigProps.REPORT_TYPES_FILTER] = filtersOverride.formats;
        }

        return config;
    }, [filtersOverride]);

    const tableConfig = useMemo(() => {
        const config = CloneUtils.deepClone(DEFAULT_TABLE_CFG);
        config[ConfigProps.HIDE_EMPTY_ROWS] = false;
        config[ConfigProps.SHOW_NOTES] = true;
        return config;
    }, []);

    const reportsToRender = useMemo(
        () =>
            ReportsUtils.sort(
                // @ts-ignore
                applyFilterCfg(reports, filterCfg, tableConfig, dynamicFlatDescriptors),
                filterCfg[ConfigProps.REPORTS_ORDER] === PropValues.DESC
            ),
        [reports, filterCfg, tableConfig, dynamicFlatDescriptors]
    );

    // Only the first load replaces the table with a loader. The Source-view re-fetch keeps it
    // mounted — unmounting would drop the table's own Source-view state and undo the toggle that
    // triggered the re-fetch in the first place.
    if (loading && reports.length === 0) {
        return <Loader />;
    }

    return (
        /**
         * Because of the table header being fixed we need to set the trottleInterval to 1ms
         * to ensure that the header is always in sync with the table body.
         * We also disable the transition to avoid flickering
         * when the header is fixed and unfixed.
         * This is all do to timing issues with the scroll transition and by disabling the transition and
         * giving it a very low throttle interval we ensure that the header is always in sync with the table body
         * when the entire table is scrolled.
         */
        <FixedElementOnScroll throttleInterval={1} transitionEnabled={false}>
            <div style={{ marginTop: 20 }} ref={masterTableRef}>
                {/* // @ts-ignore */}
                <Table
                    schemaId={null}
                    schemaName={null}
                    {...tableConfig}
                    reports={reportsToRender}
                    descriptors={descriptors}
                    dynamicFlatDescriptors={dynamicFlatDescriptors}
                    chartsRef={undefined}
                    showCharts={false}
                    // @ts-ignore
                    prefferedScale={filterCfg[ConfigProps.PREFFERED_SCALE]}
                    currencyInfo={null}
                    advancedSettingsBtn={null}
                    profileSettingsComponent={null}
                    addReportBtn={null}
                    showPeriodGrowth={false}
                    showYearlyGrowth={false}
                    nonCategorizedFlatDynamicDescriptorsMap={dynamicFlatDescriptorsMap}
                    highlightedDescriptorId={highlightedDescriptorId}
                    standardizedLabels={[]}
                    onSourceViewEnabled={() => setProvenanceRequested(true)}
                    provenanceLoading={loading}
                />
            </div>
        </FixedElementOnScroll>
    );
};

function getFilters(colsMetadata: ColumnMetadataType[]) {
    const reportColumns = colsMetadata.filter((column) => column.contentType === COLUMN_TYPE.REPORT);

    let periods = null;
    let formats = null;
    let from = null;
    let to = null;

    const foundPeriods = new Set<PeriodType>([PERIOD.FY]);

    reportColumns
        .filter((column) => column.reportProps!.period)
        .forEach((column) => {
            foundPeriods.add(column.reportProps!.period!);
        });

    if (foundPeriods.size) {
        periods = Array.from(foundPeriods);
    }

    const years = reportColumns.filter((column) => column.reportProps!.year).map((column) => column.reportProps!.year!);

    years.sort((a, b) => a - b);

    if (years.length) {
        from = years[0];
        to = years[years.length - 1];
    }
    const foundFormats = new Set<FormatType>();

    reportColumns
        .filter((column) => column.reportProps!.format)
        .forEach((column) => {
            foundFormats.add(column.reportProps!.format!);
        });

    if (foundFormats.size) {
        /**
         * Due to differences in the format casing between the automatic data import
         * and the rest of the platform here we need to capitalized the formats
         * before we override the config filters for the table.
         * In the automatic data import the values for the formats are uppercase
         * while the values for the formats in the rest of the platform are capitalized
         */
        formats = Array.from(foundFormats).map((format) => _.capitalize(format));
    }

    return {
        from,
        to,
        periods,
        formats,
    };
}
export default MasterTable;
